import { useEffect } from 'react'

import useCurrencyState from 'app/hooks/useCurrencyState'
import useEffectAfterMount from 'app/hooks/useEffectAfterMount'
import { useGetSimilarListings } from 'app/services/Query/listing/listing.query'
import useAppStore from 'app/store/app.store'
import useAuthStore, { getAuthUser } from 'app/store/auth.store'
import { WEBSITE_URL } from 'app/utils/constants/env.constants'
import { USER_ROLE } from 'app/utils/constants/role.constants'
import { LISTING_STATUS } from 'app/utils/constants/status.constants'

import useRecentlyViewed from './useRecentlyViewed'

interface Props {
  listing?: ListingModel
  isLoading?: boolean
  slug?: string
}

export const useShowListingFeature = ({ isLoading, listing, slug }: Props) => {
  const isAcceptedCookies = useAppStore((state) => state.isAcceptedCookies)
  const { setViewedItem, viewedItems } = useRecentlyViewed({ slug })

  const user = useAuthStore(getAuthUser)
  const { currency } = useCurrencyState()

  const seller = listing?.user
  const sellerAvatar = seller?.avatar ? seller.avatar.url : ''
  const sellerPaymentGateways = seller?.payment_gateways ?? []

  const auctionId = listing?.auction?.id
  const isHolidayMode = seller?.holiday_mode
  const acceptOffers = !!listing?.min_offer_price
  const availableQuantity = listing?.available_quantity

  const isSold = listing?.status === LISTING_STATUS.sold_out
  const isReserved = listing?.status === LISTING_STATUS.reserved
  const isAuctionSold = listing?.with_won_auction
  const isDisabledAddToCart = listing?.in_cart && listing?.with_accepted_offer
  const isPersonalSeller = seller?.role === USER_ROLE.PERSONAL

  const isAuthUserListing = user?.id === seller?.id
  const isDisabled =
    isHolidayMode ||
    isLoading ||
    isReserved ||
    (!isAuthUserListing && isSold) ||
    isAuctionSold ||
    isDisabledAddToCart
  const isAuction = !!auctionId

  const { data: similarItems, refetch: refetchSimilarItems } =
    useGetSimilarListings({
      listingId: String(listing?.id),
      options: { enabled: isSold }
    })

  useEffectAfterMount(() => {
    const isRefetchSimilarItems = currency && !!isSold && !!similarItems?.length

    if (isRefetchSimilarItems) refetchSimilarItems()
  }, [currency])

  useEffect(() => {
    if (slug && isAcceptedCookies) setViewedItem(slug)
  }, [slug, isAcceptedCookies])

  const url = `${WEBSITE_URL}/listing/${slug}`
  const media = listing?.attachments.map((item) => item.url) || ['']

  const categoryBreadUrl = `/${listing?.sub_category.parent.slug}`
  const subCategoryBreadUrl = `${categoryBreadUrl}/?sub_category_id=${listing?.sub_category.id}`

  return {
    seller,
    sellerAvatar,
    sellerPaymentGateways,
    auctionId,
    isHolidayMode,
    acceptOffers,
    availableQuantity,
    isSold,
    isDisabled,
    url,
    media,
    categoryBreadUrl,
    subCategoryBreadUrl,
    isAuction,
    viewedItems,
    isAuthUserListing,
    isReserved,
    isAuctionSold,
    similarItems,
    isPersonalSeller
  }
}
